import { localizeSchema } from '@/i18n/core';
// Localized storefront display.
import { currentMessages, localeLink, tr, useStoreLanguage } from '@/i18n';
import { Link } from '@/i18n/router';
import { useEffect } from 'react';
import { openCookiePreferences } from '@/utils/analyticsConsent';

const trustItems = [
  {
    icon: 'ri-truck-line',
    title: 'Envío Rápido',
    desc: 'Consulta plazos según destino',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Pago 100% Seguro',
    desc: 'Conexión cifrada y confirmación de pedido',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Calidad seleccionada',
    desc: 'Especias y hierbas de calidad contrastada',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Atención al Cliente',
    desc: 'Te ayudamos por email y teléfono',
  },
];

const Footer = () => {
  const language = useStoreLanguage();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.sal-sazon.com';

  // ── Inyectar Organization schema global en <head> ─────────────────────────
  // Presente en todas las páginas para consolidar la entidad en Knowledge Graph
  useEffect(() => {
    const scriptId = 'org-schema-global';
    if (document.getElementById(scriptId)) return; // evitar duplicados

    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'Solsazón',
      alternateName: 'Solsazon Especias Orgánicas',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        '@id': `${siteUrl}/#logo`,
        url: 'https://twidpdwinrndvvlujgij.supabase.co/storage/v1/object/public/product-images/og-cover.jpg',
        width: 1200,
        height: 630,
        caption: 'Solsazón — Especias y Hierbas Orgánicas Premium',
      },
      image: 'https://twidpdwinrndvvlujgij.supabase.co/storage/v1/object/public/product-images/og-cover.jpg',
      description: 'Tienda online de hierbas orgánicas, especias premium y mezclas artesanales. Cultivadas de forma sostenible y enviadas desde Almería a toda España.',
      foundingDate: '2026',
      foundingLocation: {
        '@type': 'Place',
        name: 'Almería, Andalucía, España',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Almería',
        addressLocality: 'Almería',
        addressRegion: 'Andalucía',
        postalCode: '04001',
        addressCountry: 'ES',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 36.834,
        longitude: -2.4637,
      },
      telephone: '+34-630-863-847',
      email: 'info@solsazon.com',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+34-630-863-847',
        contactType: 'customer service',
        email: 'info@solsazon.com',
        availableLanguage: ['Spanish', 'English'],
        areaServed: 'ES',
      },
      areaServed: [
        { '@type': 'Country', name: 'España', identifier: 'ES' },
        { '@type': 'Country', name: 'Portugal', identifier: 'PT' },
      ],
      numberOfEmployees: {
        '@type': 'QuantitativeValue',
        value: 5,
      },
      sameAs: [
        'https://www.facebook.com/solsazon',
        'https://www.instagram.com/solsazon',
        'https://x.com/solsazon',
        'https://www.youtube.com/@solsazon',
        'https://www.pinterest.com/solsazon',
        'https://www.tiktok.com/@solsazon',
        'https://www.linkedin.com/company/solsazon',
      ],
    };

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(localizeSchema(organizationSchema, currentMessages(), language));
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(scriptId);
      if (el) document.head.removeChild(el);
    };
  }, [siteUrl, language]);

  return (
    <>
      {/* Trust Bar */}
      <section className="bg-sand-50 border-t border-sand-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {tr(trustItems.map((item) => (
              <div
                key={item.title}
                className="flex flex-col items-center text-center gap-2"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-solsazon-green text-mustard-400">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <span className="font-inter text-sm font-semibold text-solsazon-dark">
                  {tr(item.title)}
                </span>
                <span className="font-inter text-xs text-solsazon-copper leading-tight">
                  {tr(item.desc)}
                </span>
              </div>
            )))}
          </div>
        </div>
      </section>

      {/* Main Footer */}
      <footer className="bg-solsazon-dark text-sand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-1">
              <div className="mb-4">
                <Link to="/" className="inline-block mb-4">
                  <img 
                    src="/images/solsazon-logo.png"
                    alt={tr("Solsazón")}
                    className="h-24 w-auto"
                  />
                </Link>
              </div>
              <p className="text-sm text-sand-200 font-inter mb-4">{tr("Especias, hierbas y mezclas gourmet de la más alta calidad. Selección cuidada para tu cocina.")}</p>
              <div className="flex space-x-3">
                <a href={localeLink("https://www.facebook.com/solsazon")} target="_blank" rel="noopener noreferrer" aria-label={tr("Solsazón en Facebook")} className="w-9 h-9 flex items-center justify-center bg-solsazon-green hover:bg-mustard-500 rounded-full transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href={localeLink("https://www.instagram.com/solsazon")} target="_blank" rel="noopener noreferrer" aria-label={tr("Solsazón en Instagram")} className="w-9 h-9 flex items-center justify-center bg-solsazon-green hover:bg-mustard-500 rounded-full transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a href={localeLink("https://www.tiktok.com/@solsazon")} target="_blank" rel="noopener noreferrer" aria-label={tr("Solsazón en TikTok")} className="w-9 h-9 flex items-center justify-center bg-solsazon-green hover:bg-mustard-500 rounded-full transition-colors cursor-pointer">
                  <i className="ri-tiktok-fill text-lg"></i>
                </a>
                <a href={localeLink("https://www.pinterest.com/solsazon")} target="_blank" rel="noopener noreferrer" aria-label={tr("Solsazón en Pinterest")} className="w-9 h-9 flex items-center justify-center bg-solsazon-green hover:bg-mustard-500 rounded-full transition-colors cursor-pointer">
                  <i className="ri-pinterest-fill text-lg"></i>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-playfair text-lg font-semibold mb-4 text-sand-50">{tr("Explorar")}</h4>
              <ul className="space-y-2 font-inter text-sm">
                <li>
                  <Link to="/" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Inicio")}</Link>
                </li>
                <li>
                  <Link to="/products" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Productos")}</Link>
                </li>
                <li>
                  <Link to="/bundles" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Packs")}</Link>
                </li>
                <li>
                  <Link to="/recipes" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Recetas")}</Link>
                </li>
                <li>
                  <Link to="/blog" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Blog")}</Link>
                </li>
                <li>
                  <Link to="/about-us" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Nosotros")}</Link>
                </li>
                <li>
                  <Link to="/fidelidad" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer flex items-center gap-1.5">
                    <i className="ri-star-line text-mustard-400 text-xs"></i>{tr("Programa de Fidelidad")}</Link>
                </li>
                <li>
                  <Link to="/mis-puntos" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer flex items-center gap-1.5">
                    <i className="ri-gift-line text-mustard-400 text-xs"></i>{tr("Mis Puntos")}</Link>
                </li>
              </ul>
            </div>

            {/* Key Links - Ayuda */}
            <div>
              <h4 className="font-playfair text-lg font-semibold mb-4 text-sand-50">{tr("Ayuda")}</h4>
              <ul className="space-y-2 font-inter text-sm">
                <li>
                  <Link to="/faq" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Preguntas Frecuentes")}</Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Contacto")}</Link>
                </li>
                <li>
                  <Link to="/order-tracking" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Seguimiento de Pedido")}</Link>
                </li>
                <li>
                  <Link to="/shipping" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Envíos y Plazos")}</Link>
                </li>
                <li>
                  <Link to="/returns" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Devoluciones")}</Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Política de Privacidad")}</Link>
                </li>
                <li>
                  <Link to="/terms" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Términos de Servicio")}</Link>
                </li>
                <li>
                  <Link to="/cookies" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Política de Cookies")}</Link>
                </li>
                <li>
                  <button type="button" onClick={openCookiePreferences} className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Preferencias de cookies")}</button>
                </li>
                <li>
                  <Link to="/sitemap" className="text-sand-200 hover:text-mustard-400 transition-colors cursor-pointer">{tr("Sitemap")}</Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-playfair text-lg font-semibold mb-4 text-sand-50">{tr("Contacto")}</h4>
              <ul className="space-y-3 font-inter text-sm">
                <li className="flex items-start space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-map-pin-line text-mustard-400"></i>
                  </div>
                  <span className="text-sand-200">{tr("Almería - España")}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-mail-line text-mustard-400"></i>
                  </div>
                  <span className="text-sand-200">{tr("info@solsazon.com")}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-phone-line text-mustard-400"></i>
                  </div>
                  <span className="text-sand-200">+34 630 863 847</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-time-line text-mustard-400"></i>
                  </div>
                  <span className="text-sand-200">{tr("Lun - Vie: 9:00 - 18:00")}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-12 pt-8 border-t border-solsazon-green/30">
            <p className="text-sm text-sand-300 font-inter text-center">{tr("© 2026 Solsazón S.L. Todos los derechos reservados.")}</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
